'use client';

import { fetchRankingProductList } from '@/api/product';
import type { ListProduct } from '@/api/types/public';
import type { CompleteTrackingParams, HomeButtonTrackingParams } from '@/app/(home-page)/types';
import AccessiblePrice from '@/components/AccessiblePrice';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import type { Product } from '@/components/Product/ProductCard';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useProductImages } from '@/hooks/useProductImages';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { useDataSpmEnabled, useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { serializeKey } from '@/utils/swr/keys';
import { RightArrowFineOutlined } from '@zeelool/icon';
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import useSWR from 'swr';

import type {
  BestsellerListConfig,
  Bestsellers2026Content,
  Bestsellers2026Props,
  Bestsellers2026ValidExposureParams,
} from './types';
import rankingLabelCartIcon from './assets/ranking-label-cart.svg';
import rankingLabelSalesIcon from './assets/ranking-label.svg';

const PRODUCT_IMAGE_RULE = [EProductImageType.PRODUCT];
const NO_HOVER_IMAGE_RULE: EProductImageType[] = [];
const MIN_PRODUCT_COUNT = 2;
const DRAG_SWITCH_THRESHOLD = 50;
const DRAG_CLICK_SUPPRESSION_THRESHOLD = 4;
const MAX_DRAG_SLOT_COUNT = 2;
const DRAG_SNAP_DURATION = 300;
const DRAG_SNAP_TRANSITION = `transform ${DRAG_SNAP_DURATION}ms ease`;
const RANKING_LABEL_VARIANTS = {
  1: {
    icon: rankingLabelSalesIcon,
    className: 'h-[20px] bg-[#d9e2ff] text-[#44427a]',
  },
  2: {
    icon: rankingLabelCartIcon,
    className: 'h-[24px] bg-[#d8fe77] text-[#282828]',
  },
} as const;
const PRODUCT_STAGE_CLASS_NAME =
  'relative grid w-full grid-cols-[clamp(200px,calc(76px_+_12.109375vw),231px)_clamp(366px,calc(46px_+_31.25vw),446px)_clamp(200px,calc(76px_+_12.109375vw),231px)] items-center justify-center gap-[clamp(64px,calc(-48px_+_10.9375vw),92px)] px-[48px] py-[24px] [--carousel-side-scale:0.546448] [--carousel-slot-distance:clamp(347px,calc(13px_+_32.6171875vw),430.5px)] xl:[--carousel-side-scale:0.517937] 3xl:grid-cols-[359.082px_638px_359.082px] 3xl:gap-[140px] 3xl:[--carousel-side-scale:0.562824] 3xl:[--carousel-slot-distance:638.541px]';
const SIDE_PRODUCT_VISUAL_CLASS_NAME =
  'h-[clamp(100px,calc(36.82px_+_6.169921875vw),115.795px)] min-w-0 opacity-60 3xl:h-[180px]';
const CENTER_PRODUCT_VISUAL_CLASS_NAME =
  'h-[clamp(183.574px,calc(23.074px_+_15.673828125vw),223.699px)] min-w-0 3xl:h-[320px]';
const CAROUSEL_PRODUCT_VISUAL_CLASS_NAME =
  'absolute left-1/2 top-1/2 h-[clamp(183.574px,calc(23.074px_+_15.673828125vw),223.699px)] w-[clamp(366px,calc(46px_+_31.25vw),446px)] origin-center select-none will-change-transform 3xl:h-[320px] 3xl:w-[638px]';
const PRODUCT_FOOTER_CLASS_NAME =
  'mx-auto flex h-[99px] w-[428px] items-end justify-between gap-[24px] px-[16px] pb-[24px]';
const CAROUSEL_BUTTON_CLASS_NAME =
  'absolute top-[calc(50%-6.29px)] z-10 flex size-[32px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple xl:top-[calc(50%-6.35px)] 3xl:top-[calc(50%-5.5px)]';
const SMALL_CAROUSEL_MOTION_CONFIG = { sideScale: 0.546448, slotDistance: 347 };
const MEDIUM_CAROUSEL_MOTION_CONFIG = { sideScale: 0.517937, slotDistance: 430.5 };
const LARGE_CAROUSEL_MOTION_CONFIG = { sideScale: 0.562824, slotDistance: 638.541 };
const SMALL_CAROUSEL_VIEWPORT = 1024;
const MEDIUM_CAROUSEL_VIEWPORT = 1280;
const LARGE_CAROUSEL_VIEWPORT = 1920;
const PRODUCT_VISUAL_CONFIG = {
  leftEdge: {
    sizes:
      '(min-width: 1920px) 359px, (min-width: 1280px) 231px, (min-width: 1024px) calc(76px + 12.109375vw), 200px',
  },
  left: {
    sizes:
      '(min-width: 1920px) 359px, (min-width: 1280px) 231px, (min-width: 1024px) calc(76px + 12.109375vw), 200px',
  },
  center: {
    sizes:
      '(min-width: 1920px) 638px, (min-width: 1280px) 446px, (min-width: 1024px) calc(46px + 31.25vw), 366px',
  },
  right: {
    sizes:
      '(min-width: 1920px) 359px, (min-width: 1280px) 231px, (min-width: 1024px) calc(76px + 12.109375vw), 200px',
  },
  rightEdge: {
    sizes:
      '(min-width: 1920px) 359px, (min-width: 1280px) 231px, (min-width: 1024px) calc(76px + 12.109375vw), 200px',
  },
} as const;

type ProductVisualVariant = keyof typeof PRODUCT_VISUAL_CONFIG;

/** 循环内坑位打标辅助：与 useSpmDomAttr 同口径（开关关闭或编码为空返回 undefined，零 DOM 差异）。 */
const buildDomSpm = (enabled: boolean, code: string | undefined | null) =>
  enabled && code ? { 'data-spm': code } : undefined;

const CAROUSEL_SLOT_POSITIONS = [-4, -3, -2, -1, 0, 1, 2, 3, 4] as const;
const CAROUSEL_SLOTS: Array<{
  position: number;
  scale: number | 'side';
  opacity: number;
}> = CAROUSEL_SLOT_POSITIONS.map((position) => {
  const distance = Math.abs(position);

  return {
    position,
    scale: distance === 0 ? 1 : distance === 1 ? 'side' : 0.28,
    opacity: distance === 0 ? 1 : distance === 1 ? 0.6 : 0,
  };
});

function getCarouselBaseTransform(position: number, scale: number | 'side') {
  const scaleValue = scale === 'side' ? 'var(--carousel-side-scale)' : String(scale);
  return `translate3d(calc(-50% + ${position} * var(--carousel-slot-distance)), -50%, 0) scale(${scaleValue})`;
}

function getCarouselMotionConfig(viewportWidth = window.innerWidth) {
  if (viewportWidth >= LARGE_CAROUSEL_VIEWPORT) return LARGE_CAROUSEL_MOTION_CONFIG;
  if (viewportWidth >= MEDIUM_CAROUSEL_VIEWPORT) return MEDIUM_CAROUSEL_MOTION_CONFIG;
  if (viewportWidth <= SMALL_CAROUSEL_VIEWPORT) return SMALL_CAROUSEL_MOTION_CONFIG;

  const progress =
    (viewportWidth - SMALL_CAROUSEL_VIEWPORT) /
    (MEDIUM_CAROUSEL_VIEWPORT - SMALL_CAROUSEL_VIEWPORT);

  return {
    sideScale:
      SMALL_CAROUSEL_MOTION_CONFIG.sideScale +
      (MEDIUM_CAROUSEL_MOTION_CONFIG.sideScale - SMALL_CAROUSEL_MOTION_CONFIG.sideScale) * progress,
    slotDistance:
      SMALL_CAROUSEL_MOTION_CONFIG.slotDistance +
      (MEDIUM_CAROUSEL_MOTION_CONFIG.slotDistance - SMALL_CAROUSEL_MOTION_CONFIG.slotDistance) *
        progress,
  };
}

function getCarouselDragScale(position: number, sideScale: number) {
  const distance = Math.abs(position);
  if (distance <= 1) return 1 - distance * (1 - sideScale);
  return Math.max(0.28, sideScale - (distance - 1) * (sideScale - 0.28));
}

function getCarouselDragOpacity(position: number) {
  const distance = Math.abs(position);
  if (distance <= 1) return 1 - distance * 0.4;
  return Math.max(0, 0.6 - (distance - 1) * 0.6);
}

type RankingProduct = ListProduct & {
  business_sku?: string | number;
  sku?: string | number;
  in_stock?: string | number;
  sell_price?: string | number;
  sale_price?: string | number;
  price?: string | number;
  original_price?: string | number;
  market_price?: string | number;
  ranking_tag?: {
    type?: 1 | 2;
    text?: string;
  };
};

function normalizeConfiguredText(value: unknown) {
  if (typeof value !== 'string') return '';
  return value.replace(/<br\s*\/?>/gi, '\n').trim();
}

function normalizePriceValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

function resolveProductPrice(
  product: RankingProduct,
  userInfo: unknown,
  currency: { name?: string },
) {
  const configuredPrice = priceInfo(userInfo, product, currency) as {
    price?: string | number;
    sell_price?: string | number;
  };
  const sellPrice =
    normalizePriceValue(configuredPrice.sell_price) ||
    normalizePriceValue(configuredPrice.price) ||
    normalizePriceValue(product.sell_price) ||
    normalizePriceValue(product.sale_price) ||
    normalizePriceValue(product.price);
  const originalPrice =
    normalizePriceValue(configuredPrice.price) ||
    normalizePriceValue(product.original_price) ||
    normalizePriceValue(product.market_price) ||
    sellPrice;

  return { price: originalPrice, sell_price: sellPrice };
}

function getTabKey(tab: BestsellerListConfig, index: number) {
  return String(tab.id ?? `${tab.product_group_id ?? 'group'}-${index}`);
}

function isRankingLabel(value: unknown): value is 1 | 2 {
  return value === 1 || value === 2;
}

function isRankingProduct(value: unknown): value is RankingProduct {
  if (!value || typeof value !== 'object') return false;

  const item = value as { id?: unknown; shop_sku?: unknown };
  return Boolean(item.id) && typeof item.shop_sku === 'string' && Boolean(item.shop_sku.trim());
}

function isAvailableProduct(product: RankingProduct) {
  const status = Number(product.status);
  const stock = Number(product.stock);

  if (Number.isFinite(status) && status !== 1) return false;
  if (Number.isFinite(stock) && stock <= 0) return false;
  return String(product.in_stock) !== '2';
}

function buildProductTrackingParams({
  product,
  productIndex,
  moduleIndex,
  indexData,
  content,
  activeTab,
  currency,
  userInfo,
}: {
  product: RankingProduct;
  productIndex: number;
  moduleIndex: number;
  indexData: Bestsellers2026Props['indexData'];
  content: Bestsellers2026Content;
  activeTab: BestsellerListConfig;
  currency: { name?: string; exchange_rate?: string | number };
  userInfo: unknown;
}): CompleteTrackingParams {
  const currentPrice = resolveProductPrice(product, userInfo, currency);
  const productGroup = activeTab.position_jump_product_group;

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(activeTab.list_title),
    mkt_index: productIndex,
    mkt_index_sub: '',
    mkt_jump_type: '2',
    mkt_jump_content: String(product.shop_sku || product.id || ''),
    shop_sku: String(product.shop_sku || ''),
    business_sku: String(product.business_sku || ''),
    product_sku: String(product.sku || ''),
    product_id: String(product.id || ''),
    product_name: String(product.short_name || product.name || ''),
    group_id: String(productGroup?.id || activeTab.product_group_id || ''),
    group_name: productGroup?.name || '',
    product_price: Number(currentPrice?.price || 0),
    current_product_price: Number(currentPrice?.sell_price || 0),
    currency: currency?.name || '',
    dollar_exchange_rate: Number(currency?.exchange_rate || 1),
  };
}

function buildValidExposureParams(
  trackingParams: CompleteTrackingParams,
): Bestsellers2026ValidExposureParams {
  return {
    module_index: trackingParams.module_index,
    module_id: trackingParams.module_id || '',
    module_title: trackingParams.module_title || '',
    module_name: trackingParams.module_name,
    module_tab_name: trackingParams.module_tab_name || '',
    mkt_index: trackingParams.mkt_index,
    mkt_index_sub: trackingParams.mkt_index_sub,
    mkt_jump_type: trackingParams.mkt_jump_type || '',
    mkt_jump_content: trackingParams.mkt_jump_content || '',
  };
}

function buildExploreTrackingParams({
  moduleIndex,
  indexData,
  content,
  activeTab,
}: {
  moduleIndex: number;
  indexData: Bestsellers2026Props['indexData'];
  content: Bestsellers2026Content;
  activeTab: BestsellerListConfig;
}): CompleteTrackingParams {
  const productGroup = content.product_group || activeTab.position_jump_product_group;

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    module_title: normalizeConfiguredText(content.layout_title),
    module_tab_name: normalizeConfiguredText(activeTab.list_title),
    mkt_index: 0,
    mkt_index_sub: '',
    mkt_jump_type: String(content.jump_type || ''),
    mkt_jump_content: String(content.Jump_target || ''),
    group_id: String(productGroup?.id || activeTab.product_group_id || ''),
    group_name: productGroup?.name || '',
  };
}

function ProductVisual({
  product,
  variant,
}: {
  product: RankingProduct;
  variant: ProductVisualVariant;
}) {
  const { productImageSrc } = useProductImages({
    productData: product as Product,
    imageRule: PRODUCT_IMAGE_RULE,
    imageHoverRule: NO_HOVER_IMAGE_RULE,
    productImagePriority: 'white-first',
  });
  const isCenter = variant === 'center';
  const productName = normalizeConfiguredText(product.name);
  const visualConfig = PRODUCT_VISUAL_CONFIG[variant];

  if (!productImageSrc) return null;

  return (
    <div
      className="relative h-full w-full"
      aria-hidden={isCenter ? undefined : true}
      data-product-visual={variant}
    >
      <Image
        src={productImageSrc}
        alt={productName}
        fill
        draggable={false}
        sizes={visualConfig.sizes}
        className="pointer-events-none object-cover mix-blend-multiply"
      />
    </div>
  );
}

function SkeletonProductVisual({ variant }: { variant: 'side' | 'center' }) {
  const isCenter = variant === 'center';

  return (
    <div
      data-skeleton-product={variant}
      className={`min-w-0 animate-pulse rounded-[12px] bg-auxiliary-light-gray ${
        isCenter ? CENTER_PRODUCT_VISUAL_CLASS_NAME : SIDE_PRODUCT_VISUAL_CLASS_NAME
      }`}
    />
  );
}

function BestsellersSkeleton({
  rootRef,
  content,
  tabs,
  activeTabKey,
}: {
  rootRef?: (node: HTMLElement | null) => void;
  content: Bestsellers2026Content;
  tabs: BestsellerListConfig[];
  activeTabKey: string;
}) {
  const heading = normalizeConfiguredText(content.layout_title);
  const showHeading = Number(content.layout_title_status) === 1 && Boolean(heading);
  const exploreText = normalizeConfiguredText(content.btn_text);
  const showExplore = Number(content.view_more_status) === 1 && Boolean(exploreText);
  const showTabList = tabs.length > 1;

  return (
    <section
      ref={rootRef}
      className="w-full overflow-hidden bg-primary-white text-[#1a1c1e]"
      style={
        Number(content.bg_color_status) === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
      aria-hidden
    >
      <header className="relative flex w-full items-center justify-between px-[48px] py-[clamp(20px,calc(-12px_+_3.125vw),28px)]">
        {showTabList ? (
          <div className="flex h-[clamp(36px,calc(4px_+_3.125vw),44px)] shrink-0 items-center rounded-[40px] bg-[#f7f7f7] p-[2px]">
            {tabs.map((tab, index) => {
              const tabKey = getTabKey(tab, index);
              const selected = tabKey === activeTabKey;

              return (
                <span
                  key={tabKey}
                  className={`flex h-[clamp(32px,3.125vw,40px)] items-center justify-center whitespace-nowrap rounded-[40px] px-[24px] font-br text-[clamp(14px,calc(6px_+_0.78125vw),16px)] leading-[clamp(17px,calc(9px_+_0.78125vw),19px)] ${
                    selected ? 'bg-primary-black font-bb text-white' : 'text-[#1a1c1e]'
                  }`}
                >
                  {normalizeConfiguredText(tab.list_title)}
                </span>
              );
            })}
          </div>
        ) : (
          <span className="w-[1px]" />
        )}

        {showHeading ? (
          <h2
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-pre-line text-center font-bb text-[clamp(24px,calc(-8px_+_3.125vw),32px)] leading-[clamp(28px,calc(-16px_+_4.296875vw),39px)]"
            style={{ color: content.layout_title_color || '#1A1C1E' }}
          >
            {heading}
          </h2>
        ) : null}

        {showExplore ? (
          <span className="flex items-center gap-[8px] font-br text-[clamp(12px,calc(-4px_+_1.5625vw),16px)] leading-[clamp(14px,calc(-6px_+_1.953125vw),19px)]">
            {exploreText}
            <RightArrowFineOutlined className="text-[20px]" aria-hidden />
          </span>
        ) : (
          <span className="w-[1px]" />
        )}
      </header>

      <div className={PRODUCT_STAGE_CLASS_NAME}>
        <SkeletonProductVisual variant="side" />
        <SkeletonProductVisual variant="center" />
        <SkeletonProductVisual variant="side" />
      </div>

      <div data-skeleton-footer className={PRODUCT_FOOTER_CLASS_NAME}>
        <div className="flex w-[204px] shrink-0 animate-pulse flex-col gap-[8px] overflow-hidden">
          <div className="flex flex-col gap-[6px]">
            <span className="h-[19px] w-[112px] rounded-[4px] bg-auxiliary-light-gray" />
            <span className="h-[18px] w-[148px] rounded-[4px] bg-auxiliary-light-gray" />
          </div>
          <span className="h-[20px] w-[132px] rounded-[2px] bg-auxiliary-light-gray" />
        </div>
        <span className="h-[40px] w-[152px] shrink-0 animate-pulse rounded-[20px] bg-auxiliary-light-gray" />
      </div>
    </section>
  );
}

function Bestsellers2026ContentComponent({
  indexData,
  content,
  tabs,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  onBtnTrack,
}: Bestsellers2026Props & {
  content: Bestsellers2026Content;
  tabs: BestsellerListConfig[];
}) {
  const t = useTranslations('homePage.bestsellers2026');
  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_pc');
  // 坑位 D 位编码：与 withSpmCnt 上报同源；模块码缺失（表外旧数据）时为空串，不硬造。
  const featuredSpmCnt = spmModule ? `${spmModule}.open_featured_product` : '';
  const viewMoreSpmCnt = spmModule ? `${spmModule}.view_more` : '';
  const shopFeaturedSpmCnt = spmModule ? `${spmModule}.shop_featured_product` : '';
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报编码同值，表外静默不打标。
  const moduleDomSpm = useSpmDomAttr(spmModule);
  const featuredDomSpm = useSpmDomAttr(featuredSpmCnt);
  const viewMoreDomSpm = useSpmDomAttr(viewMoreSpmCnt);
  const shopFeaturedDomSpm = useSpmDomAttr(shopFeaturedSpmCnt);
  // 循环内坑位（tab、主展示商品）打标开关：buildDomSpm 与 useSpmDomAttr 同口径。
  const dataSpmEnabled = useDataSpmEnabled();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const toJumpTarget = useJump();
  const reactId = useId();
  const [selectedTabKey, setSelectedTabKey] = useState(() => getTabKey(tabs[0]!, 0));
  const [emptyTabKeys, setEmptyTabKeys] = useState<Set<string>>(() => new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const exposedProductKeys = useRef(new Set<string>());
  const validExposedProductKeys = useRef(new Set<string>());
  const carouselProductRefs = useRef<Array<HTMLElement | null>>([]);
  const productStageRef = useRef<HTMLDivElement | null>(null);
  const suppressProductClickRef = useRef(false);
  const dragAnimationFrameRef = useRef<number | null>(null);
  const dragSnapTimerRef = useRef<number | null>(null);
  const pendingDragOffsetRef = useRef(0);
  const dragStateRef = useRef({
    pointerId: null as number | null,
    startX: 0,
    startY: 0,
    moved: false,
    draggingHorizontally: false,
    startedOnProduct: null as string | null,
  });
  const { ref: inViewRef, inView } = useInView({ threshold: 0, triggerOnce: true });

  const visibleTabs = useMemo(
    () => tabs.filter((tab, index) => !emptyTabKeys.has(getTabKey(tab, index))),
    [emptyTabKeys, tabs],
  );
  const activeTabIndex = visibleTabs.findIndex(
    (tab, index) => getTabKey(tab, index) === selectedTabKey,
  );
  const activeTab = visibleTabs[activeTabIndex >= 0 ? activeTabIndex : 0];
  const activeTabKey = activeTab
    ? getTabKey(activeTab, activeTabIndex >= 0 ? activeTabIndex : 0)
    : '';
  const productGroupId = Number(
    activeTab?.product_group_id || activeTab?.position_jump_product_group?.id || 0,
  );
  const rankingLabel = activeTab?.ranking_label;
  const canFetch =
    inView && productGroupId > 0 && isRankingLabel(rankingLabel) && Boolean(activeTabKey);
  const requestKey = canFetch
    ? serializeKey([
        'bestsellers2026',
        indexData.id,
        activeTabKey,
        productGroupId,
        rankingLabel,
        currency?.name,
        Boolean(token),
      ])
    : null;
  const {
    data: productData,
    error: productError,
    isLoading,
  } = useSWR<RankingProduct[] | null>(
    requestKey,
    async () => {
      const response = await fetchRankingProductList({
        group_id: productGroupId,
        ranking_tag: rankingLabel as 1 | 2,
        page: 1,
        limit: activeTab?.list_product_num || 12,
        in_stock: 3,
        stock_sort_first: 1,
      });
      const responseProducts = response.data?.list || response.data?.products;

      if (response.code !== 200 || !Array.isArray(responseProducts)) {
        throw new Error('regularListBestseller 榜单商品接口响应异常');
      }

      return responseProducts.filter(isRankingProduct).filter(isAvailableProduct);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 300000,
    },
  );
  const products = productData || [];
  const hasAttemptedFetch = productData !== undefined || Boolean(productError);

  useEffect(() => {
    if (activeTabKey && activeTabKey !== selectedTabKey) {
      setSelectedTabKey(activeTabKey);
      setCurrentIndex(0);
    }
  }, [activeTabKey, selectedTabKey]);

  useEffect(() => {
    if (
      !activeTabKey ||
      productError ||
      productData === undefined ||
      (Array.isArray(productData) && productData.length > 0)
    ) {
      return;
    }

    setEmptyTabKeys((current) => {
      if (current.has(activeTabKey)) return current;
      const next = new Set(current);
      next.add(activeTabKey);
      return next;
    });
  }, [activeTabKey, productData, productError]);

  const safeCurrentIndex = products.length
    ? ((currentIndex % products.length) + products.length) % products.length
    : 0;
  const previousIndex = products.length
    ? (safeCurrentIndex - 1 + products.length) % products.length
    : 0;
  const nextIndex = products.length ? (safeCurrentIndex + 1) % products.length : 0;
  const activeProduct = products[safeCurrentIndex];
  const previousProduct = products[previousIndex];
  const nextProduct = products[nextIndex];
  const buttonTrackingModuleId = String(indexData.blocks_advances_id || indexData.id || '');
  const buttonTrackingModuleTitle = normalizeConfiguredText(content.layout_title);
  const trackingParams = useMemo(
    () =>
      activeProduct && activeTab
        ? buildProductTrackingParams({
            product: activeProduct,
            productIndex: safeCurrentIndex,
            moduleIndex,
            indexData,
            content,
            activeTab,
            currency,
            userInfo,
          })
        : null,
    [
      activeProduct,
      activeTab,
      content,
      currency,
      indexData,
      moduleIndex,
      safeCurrentIndex,
      userInfo,
    ],
  );
  const validExposureParams = useMemo(
    () => (trackingParams ? buildValidExposureParams(trackingParams) : null),
    [trackingParams],
  );

  useEffect(() => {
    if (!inView || !trackingParams || !onExpTrack || !activeProduct) return;

    const productKey = `${activeTabKey}-${String(activeProduct.shop_sku)}`;
    if (exposedProductKeys.current.has(productKey)) return;

    try {
      onExpTrack(trackingParams);
      exposedProductKeys.current.add(productKey);
    } catch (error) {
      logger.error('2026 PC bestsellers exposure tracking error:', error);
    }
  }, [activeProduct, activeTabKey, inView, onExpTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    ts: activeProduct ? `${activeTabKey}-${String(activeProduct.shop_sku)}` : activeTabKey,
    callback: () => {
      if (!validExposureParams || !onValidExpTrack || !activeProduct) return;

      const productKey = `${activeTabKey}-${String(activeProduct.shop_sku)}`;
      if (validExposedProductKeys.current.has(productKey)) return;

      try {
        // 主展示商品有效曝光为 item 级，spm_cnt 追加坑位 D 位（open_featured_product，主展示商品位）。
        onValidExpTrack(withSpmCnt(validExposureParams, featuredSpmCnt));
        validExposedProductKeys.current.add(productKey);
      } catch (error) {
        logger.error('2026 PC bestsellers valid exposure tracking error:', error);
      }
    },
  });
  const sectionRef = useMergedRefs<HTMLElement>(inViewRef, validExposureRef);

  const trackHomeButtonClick = useCallback(
    (buttonName: string, spmCnt?: string) => {
      if (!onBtnTrack) return;

      const params: HomeButtonTrackingParams = {
        module_index: moduleIndex,
        module_id: buttonTrackingModuleId,
        module_title: buttonTrackingModuleTitle,
        module_name: indexData.component_id,
        btn_name: buttonName,
      };

      try {
        onBtnTrack(spmCnt ? withSpmCnt(params, spmCnt) : params);
      } catch (error) {
        logger.error('2026 PC bestsellers button click tracking error:', error);
      }
    },
    [
      buttonTrackingModuleId,
      buttonTrackingModuleTitle,
      indexData.component_id,
      moduleIndex,
      onBtnTrack,
    ],
  );

  const handleProductClick = useCallback(
    (featuredSpmCnt: string) => {
      if (!activeProduct || !trackingParams || !validExposureParams) return;

      try {
        // 主展示商品图点击为 open_featured_product（打开商详）；Shop Now 为
        // shop_featured_product（购买入口）。两者均跳商详，上报各自坑位级 spm_cnt；
        // 浅拷贝避免污染曝光/点击共享的 validExposureParams。
        onTrack?.(withSpmCnt(validExposureParams, featuredSpmCnt));
      } catch (error) {
        logger.error('2026 PC bestsellers click tracking error:', error);
      }

      toJumpTarget({
        jump_type: 4,
        Jump_target: `/goods-detail/${activeProduct.shop_sku}`,
      });
    },
    [activeProduct, onTrack, toJumpTarget, trackingParams, validExposureParams],
  );

  const handleExploreClick = useCallback(() => {
    if (!activeTab) return;

    // Explore 更多入口上报 view_more 坑位编码（HomeBtn_Click 与 HomeMkt_Click 同属一个坑位动作）。
    trackHomeButtonClick(normalizeConfiguredText(content.btn_text), viewMoreSpmCnt);

    try {
      onTrack?.(
        withSpmCnt(
          buildValidExposureParams(
            buildExploreTrackingParams({ moduleIndex, indexData, content, activeTab }),
          ),
          viewMoreSpmCnt,
        ),
      );
    } catch (error) {
      logger.error('2026 PC bestsellers explore tracking error:', error);
    }

    toJumpTarget(content);
  }, [
    activeTab,
    content,
    indexData,
    moduleIndex,
    onTrack,
    toJumpTarget,
    trackHomeButtonClick,
    viewMoreSpmCnt,
  ]);

  const clearCarouselAnimation = useCallback(() => {
    if (dragAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }
    if (dragSnapTimerRef.current !== null) {
      window.clearTimeout(dragSnapTimerRef.current);
      dragSnapTimerRef.current = null;
    }
  }, []);

  const resetCarouselProducts = useCallback((smooth: boolean) => {
    carouselProductRefs.current.forEach((productVisual, index) => {
      const slot = CAROUSEL_SLOTS[index];
      if (!productVisual || !slot) return;

      productVisual.style.transition = smooth ? DRAG_SNAP_TRANSITION : 'none';
      productVisual.style.transform = getCarouselBaseTransform(slot.position, slot.scale);
      productVisual.style.opacity = String(slot.opacity);
      productVisual.style.zIndex = String(10 - Math.abs(slot.position));
    });

    if (!smooth) return;

    dragSnapTimerRef.current = window.setTimeout(() => {
      carouselProductRefs.current.forEach((productVisual) => {
        productVisual?.style.removeProperty('transition');
      });
      dragSnapTimerRef.current = null;
    }, DRAG_SNAP_DURATION);
  }, []);

  const settleCarouselProducts = useCallback((dragProgress: number, onComplete: () => void) => {
    const { sideScale } = getCarouselMotionConfig();

    carouselProductRefs.current.forEach((productVisual, index) => {
      const slot = CAROUSEL_SLOTS[index];
      if (!productVisual || !slot) return;

      const position = slot.position + dragProgress;
      productVisual.style.transition = DRAG_SNAP_TRANSITION;
      productVisual.style.transform = getCarouselBaseTransform(
        position,
        getCarouselDragScale(position, sideScale),
      );
      productVisual.style.opacity = String(getCarouselDragOpacity(position));
      productVisual.style.zIndex = String(10 - Math.round(Math.abs(position)));
    });

    dragSnapTimerRef.current = window.setTimeout(() => {
      onComplete();
      carouselProductRefs.current.forEach((productVisual) => {
        productVisual?.style.removeProperty('transition');
      });
      dragSnapTimerRef.current = null;
    }, DRAG_SNAP_DURATION);
  }, []);

  const handleCarouselProductClick = useCallback(
    (variant: string | null) => {
      if (variant === 'center') {
        // 中间主展示商品图：open_featured_product（打开商详）。
        handleProductClick(featuredSpmCnt);
        return;
      }
      if (variant !== 'left' && variant !== 'right') return;

      const direction = variant === 'left' ? -1 : 1;
      clearCarouselAnimation();
      resetCarouselProducts(false);
      settleCarouselProducts(-direction, () => {
        setCurrentIndex((index) => index + direction);
      });
    },
    [
      clearCarouselAnimation,
      featuredSpmCnt,
      handleProductClick,
      resetCarouselProducts,
      settleCarouselProducts,
    ],
  );

  const syncCarouselMotionConfig = useCallback(() => {
    const productStage = productStageRef.current;
    if (!productStage) return;

    const { sideScale, slotDistance } = getCarouselMotionConfig();
    productStage.style.setProperty('--carousel-side-scale', String(sideScale));
    productStage.style.setProperty('--carousel-slot-distance', `${slotDistance}px`);
  }, []);

  const setProductStageRef = useCallback(
    (node: HTMLDivElement | null) => {
      productStageRef.current = node;
      syncCarouselMotionConfig();
    },
    [syncCarouselMotionConfig],
  );

  useLayoutEffect(() => {
    syncCarouselMotionConfig();
    window.addEventListener('resize', syncCarouselMotionConfig);

    return () => window.removeEventListener('resize', syncCarouselMotionConfig);
  }, [syncCarouselMotionConfig]);

  useLayoutEffect(() => {
    clearCarouselAnimation();
    resetCarouselProducts(false);
  }, [activeTabKey, clearCarouselAnimation, currentIndex, resetCarouselProducts]);

  useEffect(
    () => () => {
      clearCarouselAnimation();
    },
    [clearCarouselAnimation],
  );

  const handleProductPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        draggingHorizontally: false,
        startedOnProduct:
          event.target instanceof Element
            ? (event.target
                .closest('[data-carousel-product]')
                ?.getAttribute('data-carousel-product') ?? null)
            : null,
      };
      suppressProductClickRef.current = false;
      pendingDragOffsetRef.current = 0;
      clearCarouselAnimation();
      resetCarouselProducts(false);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [clearCarouselAnimation, resetCarouselProducts],
  );

  const handleProductPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (dragState.pointerId === null || dragState.pointerId !== event.pointerId) return;

    const dragOffsetX = event.clientX - dragState.startX;
    const dragOffsetY = event.clientY - dragState.startY;
    const movedDistance = Math.max(Math.abs(dragOffsetX), Math.abs(dragOffsetY));
    if (movedDistance > DRAG_CLICK_SUPPRESSION_THRESHOLD) {
      dragState.moved = true;
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && event.currentTarget.contains(activeElement)) {
        activeElement.blur();
      }
    }
    if (
      Math.abs(dragOffsetX) <= DRAG_CLICK_SUPPRESSION_THRESHOLD ||
      Math.abs(dragOffsetX) < Math.abs(dragOffsetY)
    ) {
      return;
    }

    dragState.draggingHorizontally = true;
    event.preventDefault();
    const { slotDistance } = getCarouselMotionConfig();
    const maxDragOffset = slotDistance * MAX_DRAG_SLOT_COUNT;
    pendingDragOffsetRef.current = Math.max(-maxDragOffset, Math.min(maxDragOffset, dragOffsetX));
    if (dragAnimationFrameRef.current !== null) return;

    dragAnimationFrameRef.current = window.requestAnimationFrame(() => {
      dragAnimationFrameRef.current = null;
      const { sideScale, slotDistance } = getCarouselMotionConfig();
      const dragProgress = pendingDragOffsetRef.current / slotDistance;

      carouselProductRefs.current.forEach((productVisual, index) => {
        const slot = CAROUSEL_SLOTS[index];
        if (!productVisual || !slot) return;

        const position = slot.position + dragProgress;
        productVisual.style.transform = getCarouselBaseTransform(
          position,
          getCarouselDragScale(position, sideScale),
        );
        productVisual.style.opacity = String(getCarouselDragOpacity(position));
        productVisual.style.zIndex = String(10 - Math.round(Math.abs(position)));
      });
    });
  }, []);

  const finishProductDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, cancelled = false) => {
      const dragState = dragStateRef.current;
      if (dragState.pointerId === null || dragState.pointerId !== event.pointerId) return;

      const pointerId = dragState.pointerId;
      const dragOffsetX = event.clientX - dragState.startX;
      const dragOffsetY = event.clientY - dragState.startY;
      const moved =
        dragState.moved ||
        Math.max(Math.abs(dragOffsetX), Math.abs(dragOffsetY)) > DRAG_CLICK_SUPPRESSION_THRESHOLD;
      const draggedHorizontally =
        dragState.draggingHorizontally ||
        (Math.abs(dragOffsetX) > DRAG_CLICK_SUPPRESSION_THRESHOLD &&
          Math.abs(dragOffsetX) >= Math.abs(dragOffsetY));
      const shouldActivateProduct = !cancelled && !moved && dragState.startedOnProduct !== null;
      dragState.pointerId = null;

      if (event.currentTarget.hasPointerCapture?.(pointerId)) {
        event.currentTarget.releasePointerCapture(pointerId);
      }
      clearCarouselAnimation();

      if (cancelled || moved || shouldActivateProduct) {
        suppressProductClickRef.current = true;
        window.setTimeout(() => {
          suppressProductClickRef.current = false;
        }, 0);
      }

      if (!moved) {
        if (shouldActivateProduct) handleCarouselProductClick(dragState.startedOnProduct);
        return;
      }

      if (cancelled || !draggedHorizontally || Math.abs(dragOffsetX) < DRAG_SWITCH_THRESHOLD) {
        resetCarouselProducts(true);
        return;
      }

      const { slotDistance } = getCarouselMotionConfig();
      const dragStepCount = Math.min(
        MAX_DRAG_SLOT_COUNT,
        Math.max(1, Math.round(Math.abs(dragOffsetX) / slotDistance)),
      );
      const dragDirection = dragOffsetX < 0 ? -1 : 1;
      settleCarouselProducts(dragDirection * dragStepCount, () => {
        setCurrentIndex((index) =>
          dragOffsetX < 0 ? index + dragStepCount : index - dragStepCount,
        );
      });
    },
    [
      clearCarouselAnimation,
      handleCarouselProductClick,
      resetCarouselProducts,
      settleCarouselProducts,
    ],
  );

  const handleProductStageClickCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressProductClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    suppressProductClickRef.current = false;
  }, []);

  const heading = normalizeConfiguredText(content.layout_title);
  const showHeading = Number(content.layout_title_status) === 1 && Boolean(heading);
  const exploreText = normalizeConfiguredText(content.btn_text);
  const showExplore = Number(content.view_more_status) === 1 && Boolean(exploreText);
  const productName = normalizeConfiguredText(activeProduct?.short_name || activeProduct?.name);
  const nameSuffix = normalizeConfiguredText(content.name_suffix);
  const displayProductName = [productName, nameSuffix].filter(Boolean).join(' ');
  const price = activeProduct ? resolveProductPrice(activeProduct, userInfo, currency) : undefined;
  const sellPrice = price?.sell_price
    ? formatPriceNumberWithSymbol(String(price.sell_price), currency)
    : '';
  const originalPrice =
    price?.price && price.price !== price.sell_price
      ? formatPriceNumberWithSymbol(String(price.price), currency)
      : '';
  const rankingText =
    activeProduct && activeTab && activeProduct.ranking_tag?.type === activeTab.ranking_label
      ? normalizeConfiguredText(activeProduct.ranking_tag?.text)
      : '';
  const rankingLabelVariant = isRankingLabel(activeTab?.ranking_label)
    ? RANKING_LABEL_VARIANTS[activeTab.ranking_label]
    : undefined;
  const showTabList = visibleTabs.length > 1;

  if (visibleTabs.length === 0) return null;
  if (productError || (hasAttemptedFetch && products.length < MIN_PRODUCT_COUNT)) return null;
  if (!hasAttemptedFetch || isLoading || !activeProduct || !previousProduct || !nextProduct) {
    return (
      <BestsellersSkeleton
        rootRef={inViewRef}
        content={content}
        tabs={visibleTabs}
        activeTabKey={activeTabKey}
      />
    );
  }

  const carouselProducts: Array<{
    key: string;
    product: RankingProduct;
    variant: ProductVisualVariant;
  }> = CAROUSEL_SLOT_POSITIONS.map((position) => {
    const logicalIndex = currentIndex + position;
    const productIndex = ((logicalIndex % products.length) + products.length) % products.length;
    const variant: ProductVisualVariant =
      position === 0
        ? 'center'
        : position === -1
          ? 'left'
          : position === 1
            ? 'right'
            : position < 0
              ? 'leftEdge'
              : 'rightEdge';

    return {
      key: `product-${logicalIndex}`,
      product: products[productIndex]!,
      variant,
    };
  });

  return (
    <section
      ref={sectionRef}
      data-component-id={indexData.component_id}
      className="relative w-full overflow-hidden bg-primary-white text-[#1a1c1e]"
      style={
        Number(content.bg_color_status) === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
      {...moduleDomSpm}
    >
      <header className="relative flex w-full items-center justify-between px-[48px] py-[clamp(20px,calc(-12px_+_3.125vw),28px)]">
        {showTabList ? (
          <div
            role="tablist"
            aria-labelledby={showHeading ? `${reactId}-heading` : undefined}
            className="flex h-[clamp(36px,calc(4px_+_3.125vw),44px)] shrink-0 items-center rounded-[40px] bg-[#f7f7f7] p-[2px]"
          >
            {visibleTabs.map((tab, index) => {
              const tabKey = getTabKey(tab, index);
              const selected = tabKey === activeTabKey;
              const tabLabel = normalizeConfiguredText(tab.list_title);
              // 标签坑位 D 位编码（动态标签位，序号 2 位：tab_option_01），与点击上报同源。
              const tabSpmCnt = spmModule
                ? `${spmModule}.tab_option_${String(index + 1).padStart(2, '0')}`
                : '';

              return (
                <button
                  key={tabKey}
                  id={`${reactId}-${tabKey}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`${reactId}-panel`}
                  className={`h-[clamp(32px,3.125vw,40px)] cursor-pointer whitespace-nowrap rounded-[40px] px-[24px] text-[clamp(14px,calc(6px_+_0.78125vw),16px)] leading-[clamp(17px,calc(9px_+_0.78125vw),19px)] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple ${
                    selected
                      ? 'bg-primary-black font-bb text-white'
                      : 'bg-transparent font-br text-[#1a1c1e]'
                  }`}
                  onClick={() => {
                    // 标签切换上报坑位级 spm_cnt（动态标签位，序号 2 位：tab_option_01）。
                    trackHomeButtonClick(tabLabel, tabSpmCnt);
                    setSelectedTabKey(tabKey);
                    setCurrentIndex(0);
                  }}
                  {...buildDomSpm(dataSpmEnabled, tabSpmCnt)}
                >
                  {tabLabel}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="w-[1px]" aria-hidden />
        )}

        {showHeading ? (
          <h2
            id={`${reactId}-heading`}
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-pre-line text-center font-bb text-[clamp(24px,calc(-8px_+_3.125vw),32px)] leading-[clamp(28px,calc(-16px_+_4.296875vw),39px)]"
            style={{ color: content.layout_title_color || '#1A1C1E' }}
          >
            {heading}
          </h2>
        ) : null}

        {showExplore ? (
          <button
            type="button"
            className="flex cursor-pointer items-center gap-[8px] bg-transparent p-0 font-br text-[clamp(12px,calc(-4px_+_1.5625vw),16px)] leading-[clamp(14px,calc(-6px_+_1.953125vw),19px)] text-[#1a1c1e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-purple"
            onClick={handleExploreClick}
            {...viewMoreDomSpm}
          >
            <span>{exploreText}</span>
            <RightArrowFineOutlined className="text-[20px]" aria-hidden />
          </button>
        ) : (
          <span className="w-[1px]" aria-hidden />
        )}
      </header>

      <div
        id={`${reactId}-panel`}
        role="tabpanel"
        aria-labelledby={
          showTabList
            ? `${reactId}-${activeTabKey}`
            : showHeading
              ? `${reactId}-heading`
              : undefined
        }
        className="relative w-full cursor-pointer select-none"
        onPointerDown={handleProductPointerDown}
        onPointerMove={handleProductPointerMove}
        onPointerUp={(event) => finishProductDrag(event)}
        onPointerCancel={(event) => finishProductDrag(event, true)}
        onClickCapture={handleProductStageClickCapture}
      >
        <div ref={setProductStageRef} className={PRODUCT_STAGE_CLASS_NAME}>
          <span className={`${SIDE_PRODUCT_VISUAL_CLASS_NAME} invisible`} aria-hidden />
          <span className={`${CENTER_PRODUCT_VISUAL_CLASS_NAME} invisible`} aria-hidden />
          <span className={`${SIDE_PRODUCT_VISUAL_CLASS_NAME} invisible`} aria-hidden />

          {carouselProducts.map(({ key, product, variant }, index) => {
            const slot = CAROUSEL_SLOTS[index]!;
            const productStyle = {
              opacity: slot.opacity,
              transform: getCarouselBaseTransform(slot.position, slot.scale),
              zIndex: 10 - Math.abs(slot.position),
            };

            const isCenter = variant === 'center';
            const isInteractive = isCenter || variant === 'left' || variant === 'right';

            return (
              <button
                key={key}
                ref={(node) => {
                  carouselProductRefs.current[index] = node;
                }}
                type="button"
                data-carousel-product={isInteractive ? variant : undefined}
                aria-label={
                  isInteractive
                    ? normalizeConfiguredText(product.short_name || product.name)
                    : undefined
                }
                aria-hidden={isInteractive ? undefined : true}
                tabIndex={isInteractive ? undefined : -1}
                className={`${CAROUSEL_PRODUCT_VISUAL_CLASS_NAME} bg-transparent p-0 ${
                  isInteractive
                    ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-purple'
                    : 'pointer-events-none'
                }`}
                style={productStyle}
                onClick={isInteractive ? () => handleCarouselProductClick(variant) : undefined}
                {...(isCenter ? buildDomSpm(dataSpmEnabled, featuredSpmCnt) : undefined)}
              >
                <ProductVisual product={product} variant={variant} />
              </button>
            );
          })}
        </div>
      </div>

      <div className={PRODUCT_FOOTER_CLASS_NAME}>
        <div className="flex w-[204px] shrink-0 flex-col items-start justify-end gap-[8px] overflow-hidden">
          <div className="flex w-full flex-col items-start gap-[6px]">
            <p className="w-full truncate font-br text-[16px] leading-[19px] font-normal text-[#6c6c6c]">
              {displayProductName}
            </p>
            <div className="flex w-full items-center gap-[6px] whitespace-nowrap">
              <AccessiblePrice
                value={sellPrice}
                label=""
                className="shrink-0 font-bb text-[18px] leading-[normal] text-primary-black"
              />
              {originalPrice ? (
                <AccessiblePrice
                  as="del"
                  value={originalPrice}
                  label=""
                  className="min-w-0 overflow-hidden font-br text-[16px] leading-[19px] font-normal text-ellipsis text-secondary-text"
                />
              ) : null}
            </div>
          </div>
          {rankingText && rankingLabelVariant ? (
            <span
              className={`inline-flex max-w-full shrink-0 items-center justify-center gap-[4px] rounded-[2px] px-[6px] py-[3px] font-br text-[12px] leading-[14px] font-normal ${rankingLabelVariant.className}`}
            >
              <Image
                src={rankingLabelVariant.icon}
                alt=""
                width={16}
                height={16}
                aria-hidden
                className="size-[16px] shrink-0"
              />
              <span className="truncate">{rankingText}</span>
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className="flex h-[40px] w-[152px] min-w-[128px] shrink-0 cursor-pointer items-center justify-center gap-[8px] rounded-[20px] bg-primary-black px-[16px] py-[10px] font-bb text-[14px] leading-[17px] text-white transition-colors hover:bg-primary-black-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
          onClick={() => handleProductClick(shopFeaturedSpmCnt)}
          {...shopFeaturedDomSpm}
        >
          {t('shopNow')}
        </button>
      </div>

      <button
        type="button"
        data-carousel-direction="previous"
        aria-label={normalizeConfiguredText(previousProduct.short_name || previousProduct.name)}
        className={`${CAROUSEL_BUTTON_CLASS_NAME} left-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:left-[56px]`}
        onClick={() => handleCarouselProductClick('left')}
      >
        <span className="flex size-[20px] rotate-180 items-center justify-center">
          <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
        </span>
      </button>
      <button
        type="button"
        data-carousel-direction="next"
        aria-label={normalizeConfiguredText(nextProduct.short_name || nextProduct.name)}
        className={`${CAROUSEL_BUTTON_CLASS_NAME} right-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:right-[56px]`}
        onClick={() => handleCarouselProductClick('right')}
      >
        <span className="flex size-[20px] items-center justify-center">
          <RightArrowFineOutlined className="text-[20px] [&_path]:stroke-white" aria-hidden />
        </span>
      </button>
    </section>
  );
}

export default function Bestsellers2026(props: Bestsellers2026Props) {
  const content = props.indexData.content ?? props.indexData;
  const configuredTabs = Array.isArray(content.product_list) ? content.product_list : [];
  const tabs = configuredTabs.filter(
    (tab) =>
      Number(tab.product_group_id || tab.position_jump_product_group?.id) > 0 &&
      isRankingLabel(tab.ranking_label) &&
      (configuredTabs.length === 1 || Boolean(normalizeConfiguredText(tab.list_title))),
  );

  if (Number(content.product_card_style) !== 1 || tabs.length === 0) return null;

  return <Bestsellers2026ContentComponent {...props} content={content} tabs={tabs} />;
}
